/** Supplied visible order is never repaired by clocks or range sorting.
 * Legacy seam handling is a display policy; only full source + selected
 * binding + persisted coordinate can yield verified exact assembly. */
import type { SourceTurnV1 } from './contracts';
import type { LaunchLanguageCode } from './launchLanguages';
import { assembleExactSelectedEngineRange, type ExactAssembly, type SelectedVisibleMember } from './engineCoordinate';
import { validateTranslationRevisionRecord, type AttachedTranslationRevisionV1 } from './revisionEnvelope';
import type { TranslationState } from '@/store/slices/translationSlice';

export type SourceTurnTextById = Readonly<Record<string, Readonly<SourceTurnV1>>>;
export type CaptionAssemblyStatus = { verified: true } | { verified: false;
  reason: Extract<ExactAssembly, { verified: false }>['reason'] | 'source_missing' };
export interface SelectedCaptionComposite { text: string | null; assembly: CaptionAssemblyStatus }
const pair = (id: string, language: LaunchLanguageCode) => `${id}\u0000${language}`;

export function composeSelectedCaption(
  translation: TranslationState, sourceIds: readonly string[], language: LaunchLanguageCode,
  sourceTexts: SourceTurnTextById | undefined, lineageComplete: boolean, unresolvedCount: number,
): SelectedCaptionComposite {
  const selected = sourceIds.map(id => {
    const key = pair(id, language), selection = translation.selections[key];
    const revision = selection ? translation.revisions[key]?.find(r => r.translation_revision_id === selection.selected_revision_id) : undefined;
    return { id, selection, revision, source: sourceTexts?.[id], ref: translation.turns[id] };
  });
  const texts = selected.map(m => m.revision?.text).filter((text): text is string => text !== undefined && text.length > 0);
  // A separator-free legacy display requires every member's own stored range
  // and every available namespace field. Null equality never certifies a DB
  // namespace; it merely permits unchanged legacy strings to share a seam.
  // A not-yet-selected prefix or suffix cannot inject a separator into the
  // remaining contiguous selected slices. Missing interior ranges still fail
  // adjacency; exact assembly below continues to require every visible member.
  const available = selected.filter(member => member.revision !== undefined);
  let legacyContiguous = available.length > 0;
  let namespace: readonly unknown[] | null = null;
  let previousEnd: number | null = null;
  for (const member of available) {
    const revision = member.revision;
    if (!revision || !member.ref || !validateTranslationRevisionRecord(revision).valid ||
        !('engine_range_start' in revision)) { legacyContiguous = false; break; }
    const attached = revision as AttachedTranslationRevisionV1;
    const start = attached.engine_range_start, end = attached.engine_range_end;
    let points = 0; for (const _point of attached.text) points += 1;
    if (attached.origin !== 'engine' || typeof start !== 'number' || typeof end !== 'number' ||
        !Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 0 ||
        end - start !== points || (previousEnd !== null && start !== previousEnd)) { legacyContiguous = false; break; }
    const current = [member.ref.call_id, member.ref.stable_participant_id, member.ref.source_epoch,
      member.ref.source_track_sid, attached.target_language, attached.assignment_generation,
      attached.route_generation, attached.allocation_group_id];
    if (namespace !== null && current.some((value, i) => value !== namespace![i])) { legacyContiguous = false; break; }
    namespace = current; previousEnd = end;
  }
  const fallback = texts.length > 0 ? texts.join(legacyContiguous ? '' : ' ') : null;
  if (!lineageComplete || unresolvedCount !== 0 || selected.length === 0) {
    return { text: fallback, assembly: { verified: false, reason: 'lineage_incomplete' } };
  }
  if (selected.some(member => !member.source)) {
    return { text: fallback, assembly: { verified: false, reason: 'source_missing' } };
  }
  const members: SelectedVisibleMember[] = selected.map(member => {
    const checked = member.revision ? validateTranslationRevisionRecord(member.revision) : null;
    return {
      source_turn: member.source!,
      selected_revision: checked?.valid && checked.attachment !== null
        ? member.revision as AttachedTranslationRevisionV1 : null,
      selection: member.selection?.selection_generation !== undefined ? {
        source_turn_id: member.id, target_language: language,
        selected_revision_id: member.selection.selected_revision_id,
        selection_generation: member.selection.selection_generation,
      } : null,
      coordinate: member.revision ? translation.engineCoordinatesByRevisionId[member.revision.translation_revision_id] : undefined,
    };
  });
  const exact = assembleExactSelectedEngineRange({ members, lineageComplete, unresolvedVisibleMemberCount: unresolvedCount });
  return exact.verified ? { text: exact.text, assembly: { verified: true } }
    : { text: fallback, assembly: { verified: false, reason: exact.reason } };
}
